import { ThemeConfig, PartialThemeConfig } from "@/config/theme/themeTypes";

const HEX_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const isValidColor = (v: any): v is string =>
  typeof v === "string" && HEX_REGEX.test(v.trim());

const isNonEmptyString = (v: any): v is string =>
  typeof v === "string" && v.trim().length > 0;

export function mergeThemeConfig(
  defaults: ThemeConfig,
  remote?: PartialThemeConfig | null
): ThemeConfig {
  if (!remote) return defaults;

  const colors = { ...defaults.colors };
  if (remote.colors) {
    (Object.keys(defaults.colors) as Array<keyof typeof defaults.colors>).forEach(k => {
      const v = remote.colors![k];
      if (isValidColor(v)) {
        colors[k] = v.trim();
      }
    });

    // Si el remoto pasa `primary` pero NO `primaryHover` ni `primaryActive`,
    // derivar ambos al valor de primary. Sin esto, el gradient theme
    // (createThemeCssVars) mezcla primary remoto con Hover/Active default
    // (naranjas) — produce gradients incoherentes (azul/naranja/naranja).
    // Si el cliente quiere matices Hover/Active distintos, debe pasarlos
    // explícitamente.
    if (isValidColor(remote.colors.primary)) {
      const p = remote.colors.primary.trim();
      if (!isValidColor(remote.colors.primaryHover)) colors.primaryHover = p;
      if (!isValidColor(remote.colors.primaryActive)) colors.primaryActive = p;
    }
  }

  const brand = { ...defaults.brand };
  if (remote.brand) {
    if (isNonEmptyString(remote.brand.brandName)) brand.brandName = remote.brand.brandName.trim();
    if (isNonEmptyString(remote.brand.logoUrl)) brand.logoUrl = remote.brand.logoUrl.trim();
    if (isNonEmptyString(remote.brand.faviconUrl)) brand.faviconUrl = remote.brand.faviconUrl.trim();
    if (isNonEmptyString(remote.brand.ogImage)) brand.ogImage = remote.brand.ogImage.trim();
    if (isNonEmptyString(remote.brand.contactMail)) brand.contactMail = remote.brand.contactMail.trim();
    if (isNonEmptyString(remote.brand.metaDescription)) brand.metaDescription = remote.brand.metaDescription.trim();
    if (isNonEmptyString(remote.brand.metaKeywords)) brand.metaKeywords = remote.brand.metaKeywords.trim();
    if (isNonEmptyString(remote.brand.ogUrl)) brand.ogUrl = remote.brand.ogUrl.trim();
    if (isNonEmptyString(remote.brand.termsUrl)) brand.termsUrl = remote.brand.termsUrl.trim();
    if (isNonEmptyString(remote.brand.definitionsUrl)) brand.definitionsUrl = remote.brand.definitionsUrl.trim();
    if (isNonEmptyString(remote.brand.landingDecoUrl)) brand.landingDecoUrl = remote.brand.landingDecoUrl.trim();
  }

  let recaptchaSiteKey: string | null = defaults.recaptchaSiteKey;
  if (isNonEmptyString(remote.recaptchaSiteKey)) {
    recaptchaSiteKey = remote.recaptchaSiteKey.trim();
  }

  let gtmId: string | null = defaults.gtmId || null;
  if (isNonEmptyString(remote.gtmId)) {
    gtmId = remote.gtmId.trim();
  }

  let googleAnalyticsId: string | null = defaults.googleAnalyticsId || null;
  if (isNonEmptyString(remote.googleAnalyticsId)) {
    googleAnalyticsId = remote.googleAnalyticsId.trim();
  }

  let hotjarId: string | null = defaults.hotjarId || null;
  if (isNonEmptyString(remote.hotjarId)) {
    hotjarId = remote.hotjarId.trim();
  }

  // accessMode: default permisivo ("full"). Solo "full"/"landing-only" son
  // valores válidos; cualquier otra cosa (intercepción, typo, null) cae al
  // default. No puede romper — a lo sumo deja el tenant en "full", y la
  // seguridad real igual la valida el backend.
  let accessMode = defaults.accessMode;
  if (remote.accessMode === "full" || remote.accessMode === "landing-only") {
    accessMode = remote.accessMode;
  }

  return { brand, colors, recaptchaSiteKey, gtmId, googleAnalyticsId, hotjarId, accessMode };
}
